import { ButtonGroup } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { usePriceMarketReactionData } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { MarketReactionChart } from '~/modules/market-reaction-charts/MarketReactionChart';
import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';

import { HistoricalReaction } from './historical-reaction/HistoricalReaction';
import * as S from './MarketReactionSection.styles';

interface MarketReactionSectionProps {
  entity: Entity;
  earningsId: string;
  reportingDate: string;
}

export const MarketReactionSection = ({
  entity,
  earningsId,
  reportingDate,
}: MarketReactionSectionProps) => {
  const { t } = useTranslation(['earnings']);
  const buttonOptions = useMemo(
    () => [
      t('earnings:earningsInsight.drawer.earningsRecap.marketReaction'),
      t('earnings:earningsInsight.drawer.earningsRecap.historicalReaction'),
    ],
    [t]
  );
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
  const isMarketReactionSelected = selectedButtonIndex === 0;

  const { data: earningsData, isLoading } = useGetEarningById(earningsId);

  const {
    priceData,
    fromDateEST,
    toDateEST,
    lastProportionChange,
    lastKnownPrice,
    prevCloseDate,
    lastKnownDate,
  } = usePriceMarketReactionData({
    ticker: entity?.subscribable_ticker ?? '',
    entityTag: entity.tag,
    reportingDate,
  });

  const hasEpsData = !isLoading && earningsData?.eps?.surprise !== undefined;

  return (
    <S.Root>
      {hasEpsData ? (
        <>
          <S.StyledButtonGroup>
            <ButtonGroup
              labels={buttonOptions}
              activeIndex={selectedButtonIndex}
              onClick={setSelectedButtonIndex}
            />
          </S.StyledButtonGroup>
          {!isMarketReactionSelected && (
            <HistoricalReaction entity={entity} earningsId={earningsId} />
          )}
        </>
      ) : (
        <S.Title>
          {t(
            'earnings:earningsInsight.drawer.earningsRecap.items.marketReaction'
          )}
        </S.Title>
      )}
      {(!hasEpsData || isMarketReactionSelected) && (
        <ErrorBoundaryWrapper>
          <MarketReactionChart
            fromDateEST={fromDateEST}
            toDateEST={toDateEST}
            lines={
              priceData
                ? [
                    {
                      data: priceData,
                      prevCloseDate,
                      lastProportionChange,
                      lastKnownPrice,
                      lastKnownDate,
                    },
                  ]
                : []
            }
            entities={[entity]}
          />
        </ErrorBoundaryWrapper>
      )}
    </S.Root>
  );
};
